import { useCallback, useEffect, useState } from 'react'
import {
  CampaignDetailModel,
  CommissionDetails,
  StaffUpdateCommissionInfoForCampaignRequestChangeType,
  UploadedDocument,
} from '@urbanx/agx.campaigns.client'
import {
  AgxButton,
  AgxCommissionPlan,
  AgxModal,
  AgxSpacer,
  AgxTypography,
  Icon_FeaturedIcon,
} from '@urbanx/agx.ui-components2'
import EditCommissionReason, { ChangeDetails } from './EditCommissionReason'
import {
  allowedCommissionTypes,
  EditCommissionModalPage,
} from './CommissionEnums'

interface EditCommissionModalProps {
  currentCommissionDetails?: CommissionDetails
  campaign?: CampaignDetailModel
  isSaving: boolean
  onSave: ({
    updatedCommission,
    reasonType,
    reason,
    changeEvidence,
  }: {
    updatedCommission: CommissionDetails
    reasonType: StaffUpdateCommissionInfoForCampaignRequestChangeType
    reason?: string
    changeEvidence?: UploadedDocument
  }) => Promise<boolean>
  onClose: () => void
}

const EditCommissionModal = ({
  currentCommissionDetails,
  campaign,
  isSaving,
  onSave,
  onClose,
}: EditCommissionModalProps) => {
  const [currentPage, setCurrentPage] = useState<EditCommissionModalPage>(
    EditCommissionModalPage.Reason
  )

  const [reasonDetails, setReasonDetails] = useState<ChangeDetails | undefined>(
    undefined
  )
  const [commissionDetails, setCommissionDetails] = useState<
    CommissionDetails | undefined
  >(() => currentCommissionDetails)

  const [reasonDetailsValid, setReasonDetailsValid] = useState(false)
  const [commissionDetailsValid, setCommissionDetailsValid] = useState(false)

  useEffect(() => {
    if (!reasonDetails?.reasonType) {
      setReasonDetailsValid(false)
      return
    }

    const requiresEvidence =
      reasonDetails.reasonType ===
        StaffUpdateCommissionInfoForCampaignRequestChangeType.CommissionReduction ||
      reasonDetails.reasonType ===
        StaffUpdateCommissionInfoForCampaignRequestChangeType.VendorChoiceConfirmed

    const hasEvidence = (reasonDetails.evidenceUpload?.files?.length ?? 0) > 0

    const hasOtherReason =
      reasonDetails.reasonType !==
        StaffUpdateCommissionInfoForCampaignRequestChangeType.Other ||
      !!reasonDetails.reason?.trim()

    const isUploading = reasonDetails.evidenceUpload?.isUploading ?? false

    setReasonDetailsValid(
      !isUploading && hasOtherReason && (!requiresEvidence || hasEvidence)
    )
  }, [
    reasonDetails?.reasonType,
    reasonDetails?.reason,
    reasonDetails?.evidenceUpload?.files?.length,
    reasonDetails?.evidenceUpload?.isUploading,
  ])

  useEffect(() => {
    if (currentPage === EditCommissionModalPage.UpdateSuccess) {
      const timer = setTimeout(() => {
        onClose()
        setCurrentPage(EditCommissionModalPage.Reason)
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [currentPage])

  const onReasonDetailsUpdated = useCallback(
    (updatedDetails: ChangeDetails) => {
      setReasonDetails(updatedDetails)
    },
    [setReasonDetails]
  )

  const onCommissionUpdated = useCallback(
    (updatedCommission: { value: CommissionDetails }) => {
      setCommissionDetails(updatedCommission.value)
    },
    [setCommissionDetails]
  )

  const handleSaveCommission = useCallback(async () => {
    if (!commissionDetails || !reasonDetails?.reasonType) return

    const changeEvidence = reasonDetails.evidenceUpload
      ?.files?.[0] as unknown as UploadedDocument

    const wasSaved = await onSave({
      updatedCommission: commissionDetails,
      reasonType: reasonDetails.reasonType,
      reason:
        reasonDetails.reasonType ===
        StaffUpdateCommissionInfoForCampaignRequestChangeType.Other
          ? reasonDetails.reason
          : undefined,
      changeEvidence,
    })

    if (wasSaved) {
      setCurrentPage(EditCommissionModalPage.UpdateSuccess)
    }
  }, [commissionDetails, onSave, reasonDetails])

  return (
    <AgxSpacer className="editCommissionModal">
      <AgxModal
        minWidth={
          currentPage === EditCommissionModalPage.UpdateSuccess
            ? '600px'
            : '800px'
        }
        maxWidth={
          currentPage === EditCommissionModalPage.UpdateSuccess
            ? '600px'
            : '800px'
        }
        minHeight={
          currentPage === EditCommissionModalPage.UpdateSuccess
            ? '200px'
            : undefined
        }
        maxHeight={
          currentPage === EditCommissionModalPage.UpdateSuccess
            ? '200px'
            : undefined
        }
        title={
          currentPage === EditCommissionModalPage.UpdateSuccess
            ? ''
            : currentPage === EditCommissionModalPage.Reason
              ? 'Provide reason for commission update'
              : 'Enter new commission rate'
        }
        type={`${currentPage === EditCommissionModalPage.UpdateSuccess ? 'dialog' : 'single'}`}
        variant={
          currentPage === EditCommissionModalPage.UpdateSuccess
            ? 'withoutDivider'
            : 'withDivider'
        }
        actionButtonContainerStyle={'withoutDivider'}
        content={
          <AgxSpacer
            gap="none"
            padding={{ top: 'md', bottom: 'md', left: 'xl', right: 'xl' }}
          >
            {currentPage === EditCommissionModalPage.Reason && (
              <EditCommissionReason
                defaultChangeDetails={reasonDetails}
                onChange={onReasonDetailsUpdated}
                campaignId={campaign?.campaignId || ''}
                campaignAddress={campaign?.address}
              />
            )}
            {currentPage === EditCommissionModalPage.Details && (
              <AgxCommissionPlan
                id={'commissionPlan'}
                commissionTypes={allowedCommissionTypes}
                onValueChanged={onCommissionUpdated}
                formName={'Agency Agreement'}
                defaultValue={commissionDetails}
                required
                showCommissionRangeDisclaimer
                onValidateStatusChange={setCommissionDetailsValid}
              />
            )}
            {currentPage === EditCommissionModalPage.UpdateSuccess && (
              <AgxSpacer
                direction="vertical"
                gap={'spacing-lg'}
                contentAlignment="center"
                contentJustification="center"
                fill
                padding={{ top: 'sm' }}
              >
                <Icon_FeaturedIcon />
                <AgxTypography
                  id="commissionUpdateSuccessMessage"
                  type="body_ui_strong"
                  size="medium"
                  color="var(--text-primary)"
                >
                  Commission updated successfully!
                </AgxTypography>
              </AgxSpacer>
            )}
          </AgxSpacer>
        }
        setIsDialogOpen={onClose}
        actionButtons={
          <>
            {currentPage === EditCommissionModalPage.Reason && (
              <AgxSpacer
                direction="horizontal"
                gap="spacing-lg"
                fill
                contentAlignment="baseline"
                contentJustification="center"
              >
                <AgxButton
                  id="commissionReasonCancel"
                  text="Cancel"
                  variant="hollow"
                  onClick={onClose}
                  size="large"
                  stretch
                />
                <AgxButton
                  id="commissionReasonNext"
                  text="Next"
                  variant="primary"
                  onClick={() =>
                    setCurrentPage(EditCommissionModalPage.Details)
                  }
                  disabled={!reasonDetailsValid}
                  size="large"
                  stretch
                />
              </AgxSpacer>
            )}
            {currentPage === EditCommissionModalPage.Details && (
              <AgxSpacer
                direction="horizontal"
                gap="spacing-lg"
                fill
                contentAlignment="baseline"
                contentJustification="center"
              >
                <AgxButton
                  id="commissionDetailsBack"
                  text="Back"
                  variant="hollow"
                  onClick={() => setCurrentPage(EditCommissionModalPage.Reason)}
                  size="large"
                  stretch
                  disabled={isSaving}
                />
                <AgxButton
                  id="commissionDetailsSave"
                  text="Save"
                  variant="primary"
                  onClick={handleSaveCommission}
                  disabled={
                    !commissionDetailsValid || !reasonDetailsValid || isSaving
                  }
                  size="large"
                  stretch
                />
              </AgxSpacer>
            )}
          </>
        }
      />
    </AgxSpacer>
  )
}

export default EditCommissionModal
